// Seguimiento (mini CRM): estado, vendedor, próximo seguimiento y notas; versiones guardadas; historial (la bitácora de
// ESTA cotización) con cada cambio y su antes/después — los cambios de estado y de vendedor con sus NOMBRES (contrato
// v2: Evento.cambio) —; el cuestionario; y el conector al CRM de Carlos (APAGADO hasta que él acepte).
import { useEffect, useState } from 'react';
import { ArrowRight, Check, ChevronDown, Download, ExternalLink, Pencil, Plug, RefreshCw, Save, ScrollText, Trash2 } from 'lucide-react';
import { ESTADOS_COTIZACION, EVENTOS_POR_PAGINA, EVENTOS_POR_PAGINA_MAX, MAX_DIAS_PROXIMA_FECHA, etapaDe, fechaCrmInvalida, fechaHermosillo, fechaHoraCorta, mesEnPalabras, motivoNoMandaAPapelera, opcionesDeVenta, sumarDias, usaHoyToca } from '@cotizador/compartido';
import { EstadoCotizacion as EsquemaEstado } from '@cotizador/compartido';
import type { CambioEvento, CuerpoVenta, DetalleCotizacion, EstadoCotizacion, Evento, TipoEvento, Usuario } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { bajarListaCompra, transporteSimulado, urlDe } from '../../api/cliente.ts';
import { Enlace, navegar } from '../../ruteo.tsx';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, BotonConfirmar, CampoArea, CampoSelect, CampoTexto, Chip, ErrorEnLinea, Interruptor, Seccion, Vacio } from '../../componentes/ui.tsx';
import { ETIQUETA_ESTADO, dinero, etiquetaTipoDe, kwp } from '../../logica/etiquetas.ts';
import { tonoEstado } from '../Cotizaciones.tsx';
import { detalleLegible, etiquetaRuta, valorCorto } from '../../logica/ediciones.ts';
import { textoMsi } from '../../logica/ampliacion.ts';
import { usarCotizacion } from './contexto.tsx';
import { TarjetaVersion } from './SeccionPdf.tsx';

const ETIQUETA_EVENTO: Record<TipoEvento, string> = {
  creada: 'Creada',
  editada: 'Edición',
  estado: 'Estado',
  vendedor: 'Vendedor',
  archivo_subido: 'Archivo subido',
  archivo_quitado: 'Archivo quitado',
  lectura_ia: 'Lectura de recibos',
  correccion_ia: 'Corrección con IA',
  costo_agregado: 'Costo agregado',
  costo_quitado: 'Costo quitado',
  calculo: 'Cálculo',
  revision_ia: 'Revisión con IA',
  aviso_resuelto: 'Aviso confirmado',
  version: 'Versión guardada',
  pdf: 'PDF',
  pdf_fallo: 'PDF falló',
  crm_externo: 'CRM externo',
  aprobacion: 'OK de Diego',
  lectura_autorizada: 'Tabla autorizada',
  papelera: 'Papelera',
};

function FormaCrm({ usuarios }: { usuarios: Usuario[] }) {
  const { cot, ejecutar } = usarCotizacion();
  const { puedeEn, puedeAsignar, usuario } = usarUsuario();
  const [estado, setEstado] = useState<EstadoCotizacion>(cot.estado);
  const [vendedor, setVendedor] = useState(cot.vendedor_id);
  const [seguimiento, setSeguimiento] = useState(cot.proximo_seguimiento ?? '');
  const [notas, setNotas] = useState(cot.notas_crm ?? '');
  // ADENDA z59 (C5): al marcar Ganada, QUÉ se vendió (la opción o el nivel de baterías). El servidor la exige (422): aquí se pide antes.
  const [vendida, setVendida] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [listo, setListo] = useState(false);
  const [contestando, setContestando] = useState(false);
  const [contesto, setContesto] = useState<string | null>(null);
  useEffect(() => {
    setEstado(cot.estado);
    setVendedor(cot.vendedor_id);
    setSeguimiento(cot.proximo_seguimiento ?? '');
    setNotas(cot.notas_crm ?? '');
    setVendida('');
  }, [cot.estado, cot.vendedor_id, cot.proximo_seguimiento, cot.notas_crm]);

  const cambios: { estado?: EstadoCotizacion; vendedor_id?: string; proximo_seguimiento?: string | null; notas_crm?: string | null; venta?: CuerpoVenta } = {};
  if (estado !== cot.estado) cambios.estado = estado;
  const aGanada = estado === 'ganada' && cot.estado !== 'ganada';
  const deVenta = aGanada ? opcionesDeVenta(cot.borrador) : null;
  const elegida = deVenta?.pide ? (deVenta.opciones.find((o) => `${o.opcion ?? ''}|${o.nivel ?? ''}` === vendida) ?? null) : null;
  if (aGanada && elegida) cambios.venta = { opcion: elegida.opcion, nivel: elegida.nivel };
  const faltaVenta = !!deVenta?.pide && !elegida;
  if (vendedor !== cot.vendedor_id) cambios.vendedor_id = vendedor;
  // Al pasar a Visita agendada se manda la fecha que el asesor VE en el campo (ronda 2: el servidor la quitaba sin decirlo). Ese campo pasa a
  // ser la fecha de LA VISITA: la de seguimiento que traía (a veces los 2 días automáticos) no lo es, así que al elegir Visita se vacía y el
  // asesor escribe la de la visita, o la deja vacía (7 días). Ronda 3: se guardaba la de seguimiento como fecha de la visita.
  const aVisita = estado === 'visita_agendada' && cot.estado !== 'visita_agendada';
  const cambiarEstado = (nuevo: EstadoCotizacion) => {
    const deOtro = cot.estado !== 'visita_agendada';
    if (deOtro && nuevo === 'visita_agendada' && seguimiento === (cot.proximo_seguimiento ?? '')) setSeguimiento('');
    else if (deOtro && estado === 'visita_agendada' && nuevo !== 'visita_agendada' && seguimiento === '') setSeguimiento(cot.proximo_seguimiento ?? '');
    setEstado(nuevo);
  };
  if ((seguimiento || null) !== cot.proximo_seguimiento || aVisita) cambios.proximo_seguimiento = seguimiento || null;
  if ((notas.trim() || null) !== cot.notas_crm) cambios.notas_crm = notas.trim() || null;
  const hay = Object.keys(cambios).length > 0;
  const editable = puedeEn('editar_cotizaciones', cot);
  // «Hoy toca»: si el cliente regresó (te escribió, o pidió esta propuesta rehecha en otro folio), su cuenta de mensajes sin respuesta no debe
  // pausarlo hasta enero. Sin renglón en la lista no había dónde decirlo (verificación del 2-nov, ronda 3).
  const puedeContesto = editable && usaHoyToca(usuario) && (cot.estado === 'enviada' || cot.estado === 'seguimiento') && !hay;

  async function anotarContesto() {
    setContestando(true);
    setError(null);
    setContesto(null);
    try {
      const r = await ejecutar((c) => api.resultadoSeguimiento(c.id, { revision: c.revision, accion: 'contesto' }), { reintentar: true });
      setContesto(`Anotado: «Hoy toca» lo vuelve a buscar el ${r.cotizacion.proximo_seguimiento ?? '(sin fecha)'}.`);
    } catch (e) {
      setError(e);
    } finally {
      setContestando(false);
    }
  }

  async function guardar() {
    setGuardando(true);
    setError(null);
    setListo(false);
    try {
      await ejecutar((c) => api.actualizarCrm(c.id, { revision: c.revision, ...cambios }));
      setListo(true);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="hoja pila">
      <div className="rejilla-3">
        <CampoSelect etiqueta="Estado" value={estado} disabled={!editable} onChange={(e) => cambiarEstado(e.target.value as EstadoCotizacion)} opciones={ESTADOS_COTIZACION.map((x) => ({ valor: x, texto: ETIQUETA_ESTADO[x] }))} />
        <CampoSelect
          etiqueta="Vendedor"
          value={vendedor}
          disabled={!editable}
          onChange={(e) => setVendedor(e.target.value)}
          opciones={(usuarios.length ? usuarios.filter((u) => u.rol !== 'tecnico' && ((u.activo && puedeAsignar(u.id)) || u.id === cot.vendedor_id)) : []).map((u) => ({ valor: u.id, texto: u.nombre })).concat(usuarios.some((u) => u.id === cot.vendedor_id) ? [] : [{ valor: cot.vendedor_id, texto: 'Vendedor actual' }])}
        />
        <CampoTexto
          etiqueta={estado === 'visita_agendada' ? 'Fecha de la visita' : 'Próximo seguimiento'}
          ayuda={aVisita ? 'Escribe el día de la visita: «Hoy toca» te lo pone el día después. Sin fecha, a los 7 días.' : undefined}
          type="date"
          max={sumarDias(fechaHermosillo(), MAX_DIAS_PROXIMA_FECHA)}
          error={fechaCrmInvalida(seguimiento || null, fechaHermosillo())}
          disabled={!editable}
          value={seguimiento}
          onChange={(e) => setSeguimiento(e.target.value)}
        />
      </div>
      {estado === 'cancelada' && cot.estado !== 'cancelada' && (
        <p className="nota-suave" data-ayuda-cancelada>
          Cancelada es para un folio que sobra (se rehízo o se capturó de más). Si el cliente dijo que no, usa Perdida: así «Hoy toca» ya no le escribe por otro folio.
        </p>
      )}
      {aGanada && deVenta?.pide && (
        <CampoSelect
          etiqueta={deVenta.pide === 'nivel' ? '¿Qué nivel de baterías se vendió?' : '¿Qué opción se vendió?'}
          ayuda="Con esto sale la lista de compra para Hiram. No se toma la recomendada sola."
          error={faltaVenta ? 'Elige qué se vendió para marcarla Ganada: con eso sale la lista de compra para Hiram.' : null}
          value={vendida}
          disabled={!editable}
          onChange={(e) => setVendida(e.target.value)}
          opciones={[{ valor: '', texto: 'Elige…' }, ...deVenta.opciones.map((o) => ({ valor: `${o.opcion ?? ''}|${o.nivel ?? ''}`, texto: o.texto }))]}
        />
      )}
      {aGanada && deVenta && !deVenta.pide && <p className="nota-suave">{deVenta.motivo}</p>}
      <CampoArea etiqueta="Notas del seguimiento" rows={3} maxLength={2000} disabled={!editable} placeholder="Qué dijo el cliente, qué sigue…" value={notas} onChange={(e) => setNotas(e.target.value)} />
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      {puedeContesto && (
        <div className="fila-botones" data-contesto-crm>
          <p className="nota-suave">¿El cliente te escribió o te pidió esta propuesta? Anótalo y «Hoy toca» ya no lo deja en pausa por los mensajes que no contestó.</p>
          <Boton chico variante="secundario" icono={<Check aria-hidden />} cargando={contestando} onClick={() => void anotarContesto()}>
            El cliente contestó
          </Boton>
        </div>
      )}
      {contesto && (
        <p className="nota-suave" role="status">
          {contesto}
        </p>
      )}
      {editable && (
        <div className="fila-botones fila-botones--fin">
          {listo && !hay && <span className="nota-suave">Guardado.</span>}
          <Boton variante="principal" icono={<Save aria-hidden />} cargando={guardando} disabled={!hay || faltaVenta || !!fechaCrmInvalida(seguimiento || null, fechaHermosillo())} onClick={() => void guardar()}>
            Guardar seguimiento
          </Boton>
        </div>
      )}
    </div>
  );
}

/** "Borrador → Enviada" / "Miguel Cota → Juan Carlos Cortés", con los NOMBRES que guardó el servidor (contrato v2). */
function Cambio({ c }: { c: CambioEvento }) {
  const chipEstado = (id: string | null, nombre: string | null) => {
    const e = id === null ? null : EsquemaEstado.safeParse(id);
    return e?.success ? <Chip tono={tonoEstado(e.data)}>{nombre ?? ETIQUETA_ESTADO[e.data]}</Chip> : <span>{nombre ?? '—'}</span>;
  };
  return (
    <p className="evento__cambio">
      {c.campo === 'estado' ? (
        <>
          {c.antes !== null && chipEstado(c.antes, c.antes_nombre)}
          {c.antes !== null && <ArrowRight aria-label="a" className="evento__flecha" />}
          {chipEstado(c.despues, c.despues_nombre)}
        </>
      ) : (
        <>
          {c.antes_nombre && <span className="evento__antes">{c.antes_nombre}</span>}
          {c.antes_nombre && <ArrowRight aria-label="a" className="evento__flecha" />}
          <strong>{c.despues_nombre}</strong>
        </>
      )}
    </p>
  );
}

function UnEvento({ e, usuarios }: { e: Evento; usuarios: Usuario[] }) {
  const { cot } = usarCotizacion();
  const quien = e.usuario_id ? (usuarios.find((u) => u.id === e.usuario_id)?.nombre ?? 'Alguien') : 'Sistema';
  const malo = e.tipo === 'pdf_fallo';
  const cambio = e.cambio ?? null;
  // El cambio de origen a "a mano" acompaña a cada celda corregida de la tabla: no es otro cambio (verificación
  // adversarial de la etapa 4: el historial decía «con 1 corrección» y al lado «Ver los 2 cambios»).
  const visibles = (e.ediciones ?? []).filter((x) => !/^\/lecturas\/[AB]\/historial\/\d+\/origen$/.test(x.ruta));
  return (
    <li className={['evento', malo ? 'evento--malo' : ''].join(' ')}>
      <span className="evento__punto" aria-hidden />
      <div className="evento__cuerpo">
        <p className="evento__cabeza">
          <strong>{ETIQUETA_EVENTO[e.tipo]}</strong> · {quien} · <time dateTime={e.en}>{fechaHoraCorta(e.en)}</time>
        </p>
        {/* «Cambió 2 datos: /piezas/costo_bateria, …»: las rutas de la ampliación, con su nombre («costo de la batería») */}
        {cambio ? <Cambio c={cambio} /> : e.detalle && <p className="evento__detalle">{detalleLegible(e.detalle)}</p>}
        {visibles.length > 0 && (
          <details className="plegable plegable--interior">
            <summary>
              <ChevronDown aria-hidden className="plegable__flecha" />
              {visibles.length === 1 ? 'Ver el cambio' : `Ver los ${visibles.length} cambios`}
            </summary>
            <ul className="evento__ediciones">
              {visibles.map((x, i) => (
                <li key={i}>
                  <span>{etiquetaRuta(x.ruta, cot.borrador)}</span>
                  <span>
                    {x.op === 'agregar' ? (
                      <ins>agregado</ins>
                    ) : x.op === 'quitar' ? (
                      <del>quitado</del>
                    ) : (
                      <>
                        <del>{valorCorto(x.antes, x.ruta)}</del> → <ins>{valorCorto(x.despues, x.ruta)}</ins>
                      </>
                    )}
                  </span>
                  {x.motivo && <span className="nota-suave">{x.motivo}</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </li>
  );
}

/** Pide `cuantos` eventos anteriores al cursor, en tandas de hasta EVENTOS_POR_PAGINA_MAX (lo que acepta la ruta). */
async function anterioresDesde(id: string, antes: number, cuantos: number): Promise<{ eventos: Evento[]; antes: number | null }> {
  let eventos: Evento[] = [];
  let cursor: number | null = antes;
  while (cursor !== null && eventos.length < cuantos) {
    const r = await api.historial(id, cursor, Math.min(EVENTOS_POR_PAGINA_MAX, cuantos - eventos.length));
    eventos = [...r.eventos, ...eventos];
    cursor = r.antes;
  }
  return { eventos, antes: cursor };
}

/** Historial de ESTA cotización (nube, tanda 2): el detalle trae solo los últimos EVENTOS_POR_PAGINA; «Ver anteriores»
 *  pide los de antes. Cuando el detalle se recarga tras una acción, lo ya abierto se vuelve a pedir desde el cursor nuevo
 *  (así no queda un hueco entre lo nuevo y lo viejo). */
function Historial({ usuarios }: { usuarios: Usuario[] }) {
  const { detalle, cot } = usarCotizacion();
  const cursorDetalle = detalle.eventos_antes ?? null;
  const [abiertos, setAbiertos] = useState(0);
  const [anteriores, setAnteriores] = useState<{ eventos: Evento[]; antes: number | null; desde: number | null }>({ eventos: [], antes: null, desde: null });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (abiertos === 0 || cursorDetalle === null) {
      setAnteriores({ eventos: [], antes: cursorDetalle, desde: cursorDetalle });
      return;
    }
    if (anteriores.desde === cursorDetalle && anteriores.eventos.length >= abiertos) return;
    let vivo = true;
    setCargando(true);
    anterioresDesde(cot.id, cursorDetalle, abiertos)
      .then((r) => vivo && setAnteriores({ ...r, desde: cursorDetalle }))
      .catch((e: unknown) => vivo && setError(e))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [cot.id, cursorDetalle, abiertos]); // eslint-disable-line react-hooks/exhaustive-deps

  const cursor = abiertos === 0 ? cursorDetalle : anteriores.antes;
  const eventos = [...anteriores.eventos, ...detalle.eventos];
  return (
    <div className="hoja">
      {eventos.length === 0 ? (
        <p className="nota-suave">Sin movimientos.</p>
      ) : (
        <>
          {cursor !== null && (
            <div className="historial__mas">
              <Boton variante="fantasma" chico cargando={cargando} onClick={() => setAbiertos(abiertos + EVENTOS_POR_PAGINA)}>
                Ver anteriores
              </Boton>
            </div>
          )}
          <ol className="eventos">
            {eventos.map((e) => (
              <UnEvento key={e.id} e={e} usuarios={usuarios} />
            ))}
          </ol>
        </>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </div>
  );
}

function Versiones() {
  const { detalle, ejecutar, cot } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const versiones = [...detalle.versiones].sort((a, b) => b.numero - a.numero);
  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await ejecutar((c) => api.guardarVersion(c.id, c.revision, motivo.trim()));
      setMotivo('');
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }
  return (
    <div className="pila">
      {versiones.length === 0 && <Vacio titulo="Sin versiones">Cada PDF generado guarda una versión que ya no cambia.</Vacio>}
      {versiones.map((v) =>
        v.pdf ? (
          <TarjetaVersion key={v.numero} v={v} />
        ) : (
          <div key={v.numero} className="version">
            <div className="version__cuerpo">
              <p className="version__titulo">Versión {v.numero} · sin PDF</p>
              <p className="version__meta">
                {fechaHoraCorta(v.creada_en)} · {v.motivo}
                {v.contado_recomendada !== null && ` · ${dinero(v.contado_recomendada)} contado${v.kwp_recomendada !== null ? ` (${kwp(v.kwp_recomendada)})` : ''}`}
              </p>
            </div>
          </div>
        ),
      )}
      {puedeEn('editar_cotizaciones', cot) && (
        <div className="fila-guardar-version">
          <CampoTexto etiqueta="Guardar una versión sin PDF" placeholder="Motivo (ej. antes de cambiar el precio)" maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          <Boton cargando={guardando} disabled={motivo.trim().length < 2} onClick={() => void guardar()}>
            Guardar versión
          </Boton>
        </div>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </div>
  );
}

/** ADENDA z59 · verificación del 30-oct: «Volver a congelar». Corrige qué se vendió o congela otra vez la lista con la cotización de
 *  hoy (después de un cambio, o una Ganada de antes sin registro). El servidor la anota como reemplazo (Diego lo ve).
 *  P4 (verificación 2 del 30-oct): volver a congelar conserva la lista del mes, el FIX y el día de la VENTA; solo Diego puede pedir que
 *  la meta vaya con la lista y el FIX de hoy (el interruptor solo lo ve él; el servidor da 403 a cualquier otro). */
function VolverACongelar({ urgente, sinRegistro }: { urgente: boolean; sinRegistro: boolean }) {
  const { cot, ejecutar } = usarCotizacion();
  const { puede, puedeEn } = usarUsuario();
  const [elegida, setElegida] = useState('');
  const [deHoy, setDeHoy] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [listo, setListo] = useState(false);
  const de = opcionesDeVenta(cot.borrador);
  if (!de.pide || !puedeEn('editar_cotizaciones', cot)) return null;
  const esDiego = puede('ver_costos');
  const o = de.opciones.find((x) => `${x.opcion ?? ''}|${x.nivel ?? ''}` === elegida) ?? null;
  async function congelar() {
    if (!o) return;
    setGuardando(true);
    setError(null);
    setListo(false);
    try {
      await ejecutar((c) => api.actualizarCrm(c.id, { revision: c.revision, estado: 'ganada', venta: { opcion: o.opcion, nivel: o.nivel, ...(esDiego && deHoy ? { meta_de_hoy: true } : {}) } }));
      setListo(true);
      setElegida('');
      setDeHoy(false);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }
  return (
    <div className="pila" aria-label="Volver a congelar la venta">
      <CampoSelect
        etiqueta={urgente ? 'Vuelve a congelarla: ¿qué se vendió?' : sinRegistro ? 'Congelar su lista de compra: ¿qué se vendió?' : 'Corregir qué se vendió'}
        ayuda={
          sinRegistro
            ? 'Es una venta de antes de que el cotizador guardara qué se vendió: elige la opción solo si se va a comprar (la lista sale de la cotización como está hoy).'
            : 'Rearma la lista de compra con la cotización de hoy, con la lista de proveedores y el FIX del día de la venta. La anterior queda anotada (la ve Diego).'
        }
        value={elegida}
        onChange={(e) => setElegida(e.target.value)}
        opciones={[{ valor: '', texto: 'Elige…' }, ...de.opciones.map((x) => ({ valor: `${x.opcion ?? ''}|${x.nivel ?? ''}`, texto: x.texto }))]}
      />
      {esDiego && !sinRegistro && (
        <Interruptor
          etiqueta="Con la lista del mes y el FIX de hoy"
          detalle="La meta deja de ser la del día de la venta: lo decides tú (P4) y la hoja de Hiram lo dice."
          marcado={deHoy}
          alCambiar={setDeHoy}
        />
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      <div className="fila-botones">
        {listo && <span className="nota-suave">Congelada otra vez.</span>}
        <Boton chico variante={urgente ? 'principal' : 'fantasma'} icono={<RefreshCw aria-hidden />} cargando={guardando} disabled={!o} onClick={() => void congelar()}>
          Volver a congelar
        </Boton>
      </div>
    </div>
  );
}

/** ADENDA z59: lo que se vendió (todos) y, SOLO para Diego, la lista de compra congelada para Hiram (P1–P4). El servidor ya le
 *  quitó `lista_compra` a quien no es Diego; aquí además solo se enseña con `ver_costos`. Si la cotización cambió después de la venta
 *  se dice en ROJO a todos y la hoja no sale hasta volver a congelarla (verificación del 30-oct). */
function VentaYLista({ detalle }: { detalle: DetalleCotizacion }) {
  const { cot } = usarCotizacion();
  const { puede } = usarUsuario();
  const [error, setError] = useState<unknown>(null);
  const [bajando, setBajando] = useState<string | null>(null);
  const v = detalle.venta ?? null;
  const l = puede('ver_costos') ? (detalle.lista_compra ?? null) : null;
  const bajar = async (version: 'hiram' | 'diego', formato: 'html' | 'sobre', abrir: boolean) => {
    const clave = `${version}-${formato}-${abrir ? 'ver' : 'bajar'}`;
    setError(null);
    setBajando(clave);
    // La pestaña se abre ANTES de esperar (si no, el navegador del iPhone la bloquea) y luego recibe la hoja.
    const ventana = abrir ? window.open('', '_blank') : null;
    try {
      const blob = await bajarListaCompra(cot.id, version, formato);
      // Con el servidor de verdad: ya se sabe que la emite (si no, el error se ve AQUÍ, en rojo, en vez de sacar a Diego de la app a
      // un JSON crudo); se abre la URL directa, que se sirve aislada (CSP «sandbox»). En modo simulado, los bytes del simulado.
      const url = transporteSimulado() ? URL.createObjectURL(blob) : urlDe('listaDeCompra', { params: { id: cot.id }, consulta: { version, formato, ...(abrir ? {} : { descargar: 'si' }) } });
      if (ventana) ventana.location.href = url;
      else {
        const a = document.createElement('a');
        a.href = url;
        if (transporteSimulado()) a.download = `${cot.folio}-${formato === 'sobre' ? 'costeo-crm.json' : `lista-de-compra${version === 'diego' ? '-diego' : ''}.html`}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      if (url.startsWith('blob:')) window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      ventana?.close();
      setError(e);
    } finally {
      setBajando(null);
    }
  };
  const cambio = v?.cambio_despues ?? null;
  return (
    <div className="hoja pila" aria-label="Venta y lista de compra">
      {v ? (
        <p>
          <strong>Se vendió:</strong> {v.descripcion}
          {v.contado_con_iva !== null ? ` · ${dinero(v.contado_con_iva)} de contado` : ''}
          {v.marcada_sin_registro ? ` · Ganada desde el ${fechaHoraCorta(v.en)} (antes de que se guardara qué se vendió)` : ` · la marcó ${v.por_nombre} el ${fechaHoraCorta(v.en)}`}
          {v.congelada ? ` · la volvió a congelar ${v.congelada.por_nombre} el ${fechaHoraCorta(v.congelada.en)}` : ''}
        </p>
      ) : (
        <p className="nota-suave">Ganada sin registro de qué opción se vendió.</p>
      )}
      {v && v.pdf_al_dia === false && (
        <p className="aviso aviso--amarillo">{`Los precios de la cotización cambiaron después de su último PDF (V${v.version_pdf}): confirma con el cliente qué precio aceptó.`}</p>
      )}
      {cambio && !l && <p className="aviso aviso--rojo">{`${cambio} Avísale a Diego.`}</p>}
      {l && l.estado === 'cambio' && <p className="aviso aviso--rojo">{`No se emite la hoja de compra. ${l.motivo ?? ''}`}</p>}
      {l && l.reemplaza && <p className="aviso aviso--amarillo">{l.reemplaza}</p>}
      {l && l.estado === 'lista' && (
        <>
          <p>
            <strong>Lista de compra para Hiram:</strong> meta {dinero(l.meta_mxn ?? 0)} sin IVA · {l.partidas} partidas · listas de {l.mes_listas ? mesEnPalabras(l.mes_listas) : '—'}
            {l.techo_verde_mxn !== null ? ` · techo en verde ${dinero(l.techo_verde_mxn)}` : ''}
          </p>
          {(l.alertas ?? []).map((a, i) => (
            <p key={i} className="aviso aviso--rojo">
              {a}
            </p>
          ))}
          {l.sin_proveedor_confiable > 0 && <p className="aviso aviso--amarillo">{`${l.sin_proveedor_confiable} ${l.sin_proveedor_confiable === 1 ? 'partida no tiene' : 'partidas no tienen'} proveedor confiable (falta la carta de distribuidor autorizado o las condiciones por escrito): su meta se queda en lo cotizado.`}</p>}
          {l.avisos.length > 0 && (
            <ul className="nota-suave">
              {l.avisos.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          )}
          <div className="fila-botones">
            <Boton chico icono={<Download aria-hidden />} cargando={bajando === 'hiram-html-bajar'} onClick={() => void bajar('hiram', 'html', false)}>
              Bajar la de Hiram
            </Boton>
            <Boton chico icono={<ExternalLink aria-hidden />} cargando={bajando === 'hiram-html-ver'} onClick={() => void bajar('hiram', 'html', true)}>
              Ver la de Hiram
            </Boton>
            <Boton chico variante="fantasma" icono={<ExternalLink aria-hidden />} cargando={bajando === 'diego-html-ver'} onClick={() => void bajar('diego', 'html', true)}>
              La mía (con techo y margen)
            </Boton>
            <Boton chico variante="fantasma" icono={<Download aria-hidden />} cargando={bajando === 'diego-sobre-bajar'} onClick={() => void bajar('diego', 'sobre', false)}>
              Sobre para el CRM
            </Boton>
          </div>
          <p className="nota-suave">Para mandársela a Hiram, usa «Bajar la de Hiram» y reenvía el archivo: el enlace de «Ver» solo abre con tu sesión.</p>
        </>
      )}
      {l && l.estado === 'falla' && <p className="aviso aviso--rojo">{`La lista de compra NO se armó (no se emite la hoja): ${l.motivo ?? ''}`}</p>}
      {l && (l.estado === 'no_aplica' || l.estado === 'sin_registro') && <p className="nota-suave">{l.motivo}</p>}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      {/* Verificación 2 del 30-oct: el vendedor también corrige qué se vendió (volver a congelar ya no mueve la meta: P4). Urgente si la
          cotización cambió después de la venta (o ya tiene opciones y no se dijo cuál); una Ganada de antes sin registro, sin prisa. */}
      <VolverACongelar urgente={!!cambio || l?.estado === 'cambio'} sinRegistro={!v} />
    </div>
  );
}

export function SeccionSeguimiento() {
  const { cot, detalle } = usarCotizacion();
  const { config, puede, puedeEn, usuario } = usarUsuario();
  const editable = puedeEn('editar_cotizaciones', cot);
  // PAPELERA (Diego, 13-oct): la misma regla que el servidor (Diego cualquiera; un vendedor, sus borradores).
  const puedeBorrar = motivoNoMandaAPapelera({ id: usuario.id, rol: usuario.rol, activo: true }, cot) === null;
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState<unknown>(null);
  const borrar = async () => {
    setBorrando(true);
    setErrorBorrar(null);
    try {
      await api.mandarAPapelera(cot.id, cot.revision);
      navegar('/cotizaciones');
    } catch (e) {
      setErrorBorrar(e);
      setBorrando(false);
    }
  };
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [errorCrm, setErrorCrm] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    api
      .usuarios()
      .then((r) => setUsuarios(r.usuarios))
      .catch(() => setUsuarios([]));
  }, []);
  const q = cot.borrador.cuestionario;

  const probarCrm = async () => {
    setEnviando(true);
    setErrorCrm(null);
    try {
      await api.enviarCrmExterno(cot.id);
    } catch (e) {
      setErrorCrm(e);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="pila pila--grande">
      <Seccion
        titulo="Seguimiento"
        acciones={
          puedeBorrar ? (
            <BotonConfirmar
              pregunta={puede('administrar_papelera') ? 'Se va a la papelera; la puedes recuperar.' : 'Se va a la papelera; Diego la puede recuperar.'}
              icono={<Trash2 aria-hidden />}
              etiquetaAccesible={`Borrar ${cot.folio}`}
              cargando={borrando}
              alConfirmar={() => void borrar()}
            >
              Borrar
            </BotonConfirmar>
          ) : undefined
        }
      >
        <ErrorEnLinea error={errorBorrar} alCerrar={() => setErrorBorrar(null)} />
        <FormaCrm usuarios={usuarios} />
      </Seccion>

      {detalle.venta !== undefined && (
        <Seccion titulo="Venta" descripcion={puede('ver_costos') ? 'Lo que se vendió y la lista de compra congelada (meta y proveedor por partida) para reenviársela a Hiram.' : 'Lo que se vendió.'}>
          <VentaYLista detalle={detalle} />
        </Seccion>
      )}

      <div className="rejilla-lateral">
        <Seccion
          titulo="Historial"
          descripcion="Bitácora de esta cotización: quién hizo qué y cuándo, con cada cambio y su antes y después."
          acciones={
            puede('ver_bitacora') ? (
              <Enlace a="/configuracion/bitacora" className="boton boton--fantasma boton--chico">
                <ScrollText aria-hidden />
                <span>Bitácora del sistema</span>
              </Enlace>
            ) : undefined
          }
        >
          <Historial usuarios={usuarios} />
        </Seccion>

        <div className="pila pila--grande">
          <Seccion titulo="Versiones">
            <Versiones />
          </Seccion>

          <Seccion
            titulo="Cuestionario"
            acciones={
              editable ? (
                <Enlace a={`/cotizaciones/${cot.id}/cuestionario/editar`} className="boton boton--secundario boton--chico">
                  <Pencil aria-hidden />
                  <span>Editar</span>
                </Enlace>
              ) : undefined
            }
          >
            <div className="hoja">
              <dl className="resumen__lista">
                <div className="resumen__fila">
                  <dt>Cliente</dt>
                  <dd>
                    {q.cliente.nombre}
                    {q.cliente.telefono ? ` · ${q.cliente.telefono}` : ''}
                    {q.cliente.correo ? ` · ${q.cliente.correo}` : ''}
                  </dd>
                </div>
                {q.tipo !== 'ampliacion_baterias' && (
                  <div className="resumen__fila">
                    <dt>Recibo a nombre de</dt>
                    <dd>{q.titular.es_el_cliente ? 'El cliente' : q.titular.nombre}</dd>
                  </div>
                )}
                <div className="resumen__fila">
                  <dt>Tipo</dt>
                  <dd>
                    {etiquetaTipoDe(q)}
                    {q.cliente.giro ? ` · ${q.cliente.giro}` : ''}
                  </dd>
                </div>
                <div className="resumen__fila">
                  <dt>Etapa</dt>
                  <dd>{etapaDe(q) === 'final' ? 'Final (visita hecha)' : 'Precotización'}</dd>
                </div>
                {/* Ampliación de baterías: sin recibo ni consumo; lo que se amplía (lo demás, en «Ampliación») */}
                {q.tipo === 'ampliacion_baterias' ? (
                  <>
                    <div className="resumen__fila">
                      <dt>Sistema</dt>
                      <dd>
                        {cot.borrador.piezas?.bateria ? `${cot.borrador.piezas.bateria.marca} ${cot.borrador.piezas.bateria.modelo}` : 'Batería por elegir'}
                        {cot.borrador.piezas?.actuales != null ? ` · tiene ${cot.borrador.piezas.actuales}` : ''}
                        {cot.borrador.piezas?.nuevas != null ? ` · suma ${cot.borrador.piezas.nuevas}` : ''}
                      </dd>
                    </div>
                    {/* el precio como lo imprime el PDF: los 3 MSI del cálculo (msi_3; arriba de $500,000 de lista no hay) */}
                    <div className="resumen__fila">
                      <dt>Precio</dt>
                      <dd>{cot.borrador.calculo_piezas ? `${dinero(cot.borrador.calculo_piezas.contado_con_iva)} contado con IVA · ${textoMsi(cot.borrador.calculo_piezas)}` : 'Sin precio todavía'}</dd>
                    </div>
                  </>
                ) : (
                <>
                <div className="resumen__fila">
                  <dt>Consumo</dt>
                  <dd>
                    {q.consumo.es_nuevo ? 'Nuevo, estimado' : `${q.dos_medidores ? 'Dos medidores' : 'Un medidor'} · ${q.facturacion === 'no_se' ? 'facturación por leer' : q.facturacion} · ${q.tarifa === 'no_se' ? 'tarifa por leer' : q.tarifa}`}
                    {q.consumo.cambio !== 'igual' && ` · ${q.consumo.cambio === 'aumenta' ? 'va a subir' : 'va a bajar'}${q.consumo.cambio_detalle ? ` (${q.consumo.cambio_detalle})` : ''}`}
                  </dd>
                </div>
                <div className="resumen__fila">
                  <dt>Ampliación</dt>
                  <dd>{q.ampliacion.es_ampliacion ? `Sí${q.ampliacion.existente?.kwp ? ` · ${q.ampliacion.existente.kwp} kWp` : ''}${q.ampliacion.existente?.inversor ? ` · ${q.ampliacion.existente.inversor}` : ''}` : 'No'}</dd>
                </div>
                </>
                )}
                {q.notas_libres && (
                  <div className="resumen__fila resumen__fila--larga">
                    <dt>Notas del vendedor</dt>
                    <dd>{q.notas_libres}</dd>
                  </div>
                )}
              </dl>
            </div>
          </Seccion>

          <Seccion titulo="CRM de la plataforma">
            <div className="hoja pila">
              <p className="crm-externo">
                <Plug aria-hidden />
                <span>
                  <Chip tono={config.crm_externo.activo ? 'verde' : 'neutro'}>{config.crm_externo.activo ? 'Conectado' : 'Apagado'}</Chip> {config.crm_externo.motivo}
                </span>
              </p>
              {cot.crm_externo.enviado && <p className="nota-suave">Enviada: {cot.crm_externo.id_externo}</p>}
              {editable && (
                <Boton chico disabled={!config.crm_externo.activo} cargando={enviando} onClick={() => void probarCrm()}>
                  Mandar al CRM de la plataforma
                </Boton>
              )}
              <ErrorEnLinea error={errorCrm} alCerrar={() => setErrorCrm(null)} />
            </div>
          </Seccion>
        </div>
      </div>
    </div>
  );
}
